/**
 * 处理响应数据
 * @param response 响应数据
 * @returns 处理后的响应数据
 */
export const handleResponse = (response: any): any => {
  if (response?.code === 0) {
    return response.data;
  }
  throw new Error(response?.message || '请求失败');
};
// 包装请求参数
import type { RequestConfig } from '@umijs/max';
import { getLocale } from '@umijs/max';
import { message, notification } from 'antd';
import { API_CONFIG } from '../../../config/env';
import {
  getTime,
  generateRandomString,
  createStandardUuid,
  aesEncrypt,
  aesDecrypt,
  dataSign
} from '../../../public/scripts/utils.js'

export const objData = (data: Record<string, any>, encryptBool?: boolean) => {
  // 使用配置项控制是否加密，如果没有传入encryptBool，则使用配置项
  // const shouldEncrypt = encryptBool !== undefined ? encryptBool : API_CONFIG.encrypt;
  // 加密方案
  let requestData ={
    "time": getTime(),
    "nonce_str": generateRandomString(16),
    "message_id": createStandardUuid(),
    "client_id": localStorage.getItem('uuid') || createStandardUuid(), // uuid
    "data": '',
    "sign": '',
  }
  if (API_CONFIG.encrypt) {
    requestData.data = data ? aesEncrypt(JSON.stringify(data)) : null;
    requestData.sign = dataSign(requestData)
    console.log('加密方案 requestData', requestData)
  } else {
    requestData = data ? data : null;
  }
  return requestData
}

// 加密方案
export const upLoadObjData = (data: Record<string, any>, encryptBool?: boolean) => {
  let requestData ={
    "time": getTime(),
    "nonce_str": generateRandomString(16),
    "message_id": createStandardUuid(),
    "client_id": localStorage.getItem('uuid') || createStandardUuid(), // uuid
    "data": '',
    "sign": '',
  }
  requestData.data = data ? aesEncrypt(JSON.stringify(data)) : null;
  requestData.sign = dataSign(requestData)
  return requestData
}

// 错误处理方案： 错误类型
export enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}

// 与后端约定的响应数据格式
export interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

// 请求拦截器
export const requestInterceptors: RequestConfig['requestInterceptors'] = [
  (config: any) => {
    // 拦截请求配置，进行个性化处理
    const url = config?.url;
    console.log('[Request] env:', API_CONFIG.env, 'baseURL:', API_CONFIG.baseURL, 'url:', url);
    // dev 环境强制使用相对路径，确保请求走本地 proxy
    // if (API_CONFIG.env === 'dev') {
    //   config.baseURL = '/';
    // }
    // 根据当前 locale 动态设置 accept-language 请求头
    const locale = getLocale();
    const langMap: Record<string, string> = { 'zh-CN': 'cn', 'en-US': 'en', 'zh-TW': 'tw' };
    const lang = langMap[locale] || 'cn';
    config.headers = {
      ...config.headers,
      'accept-language': lang,
    };
    if (url != '/api/user/login') {
      // TODO: localStorage 存在 XSS 泄漏风险，建议后续迁至 httpOnly Cookie + CSP
      config.headers.token = localStorage.getItem('houseToken')
    }
    return config;
  },
];

// 响应拦截器
export const responseInterceptors: RequestConfig['responseInterceptors'] = [
  (response) => {
    // 拦截响应数据，进行个性化处理
    // 尝试解密响应数据
    const originalData = response.data;
    console.log('原始响应数据:', originalData);

    // 只有在加密配置开启时才尝试解密
    if (API_CONFIG.encrypt && originalData && originalData.code === 0 && originalData.data?.data) {
      // 尝试解密数据
        const decryptedStr = aesDecrypt(originalData.data.data);
        console.log('解密后的数据:', decryptedStr);
        // 尝试解析解密后的数据
        try {
          const decryptedData = JSON.parse(decryptedStr);
          console.log('解密并解析后的数据:', decryptedData);
          response.data = {
            ...originalData,
            data: decryptedData,
          };
        } catch (e) {
          // 如果解密后的数据不是JSON格式，则直接返回解密后的字符串
          console.log('解密后的数据（非JSON）:', decryptedStr);
          response.data = {
            ...originalData,
            data: decryptedStr,
          };
        }
    } else {
      // 处理登录过期的情况
      console.log('处理后的响应数据:', response.data);
      if (response.data?.code === 0) {
        response.data = {
          ...originalData,
          data: originalData.data?.data || '',
        };
      } else if (response.data?.code === 9000008 || response.data?.code === 90000009) {
        localStorage.removeItem('houseToken');
        window.location.href = '/user/login';
      }
    }

    return response;
  },
];

// 错误处理配置
export const errorConfig: RequestConfig = {
  // 基础URL配置
  baseURL: API_CONFIG.baseURL,
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { code, data, msg, errorCode, errorMessage, showType } = res as any;
      // 兼容 code 为字符串 "0" 的情况，避免误判成功响应为错误
      if (code !== 0 && code !== '0') {
        const error: any = new Error(msg || errorMessage || '请求失败');
        error.name = 'BizError';
        error.info = { errorCode, errorMessage: msg || errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        message.error(`Response status:${error.response.status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // `error.request` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },
  // 应用请求拦截器
  requestInterceptors,
  // 应用响应拦截器
  responseInterceptors,
};
